<template>
  <v-menu
    v-model="menuOpen"
    :close-on-content-click="false"
    bottom
    offset-y
    max-width="400px"
    min-width="400px"
    transition="scale-transition"
    class="mt-2"
  >
    <template v-slot:activator="{ on, attrs }">
      <v-btn
        outlined
        rounded
        color="primary"
        class="date-range-button px-4 py-2"
        v-bind="attrs"
        v-on="on"
      >
        <span class="font-weight-medium">{{ buttonText }}</span>

        <font-awesome-icon
          :icon="['fas', 'chevron-down']"
          size="xs"
          class="ml-2"
        />
      </v-btn>
    </template>

    <v-card class="date-range-dropdown-card pa-0">
      <v-card-title class="date-range-header px-4 pt-4 pb-3">
        <span class="text-subtitle-1 font-weight-bold darkblue--text">Date Range</span>

        <v-spacer />

        <v-btn
          icon
          small
          color="grey darken-1"
          @click="menuOpen = false"
        >
          <font-awesome-icon :icon="['fas', 'times']" />
        </v-btn>
      </v-card-title>

      <v-divider />

      <v-card-text class="px-4 py-4">
        <date-range-picker
          :loading="loading"
          :initial-start-date="initialStartDate"
          :initial-end-date="initialEndDate"
          @apply-date-range="onApplyDateRange"
          @clear-date-range="onClearDateRange"
        />
      </v-card-text>
    </v-card>
  </v-menu>
</template>

<script>
import dates from 'src/utils/dates'

export default {
  name: 'DateRangePickerButton',
  components: {
    DateRangePicker: () => import('./DateRangePicker'),
  },
  props: {
    loading: {
      type: Boolean,
      default: false,
    },
    initialStartDate: {
      type: String,
      default: null,
    },
    initialEndDate: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      menuOpen: false,
    }
  },
  computed: {
    buttonText() {
      // If we have both dates, show the range
      if (this.initialStartDate && this.initialEndDate) {
        const startFormatted = dates.getFormattedDate(this.initialStartDate, 'MMM DD')
        const endFormatted = dates.getFormattedDate(this.initialEndDate, 'MMM DD, YYYY')

        // Check if it's a standard preset range
        const presetText = this.getPresetText()
        if (presetText) {
          return `${presetText} (${startFormatted} - ${endFormatted})`
        }

        return `${startFormatted} - ${endFormatted}`
      }

      // If we only have start date
      if (this.initialStartDate) {
        return `From ${dates.getFormattedDate(this.initialStartDate, 'MMM DD, YYYY')}`
      }

      // If we only have end date
      if (this.initialEndDate) {
        return `Until ${dates.getFormattedDate(this.initialEndDate, 'MMM DD, YYYY')}`
      }

      // Default text
      return 'Select Date Range'
    },
  },
  methods: {
    getPresetText() {
      if (!this.initialStartDate || !this.initialEndDate) {
        return null
      }

      const startDate = dates.get(this.initialStartDate)
      const endDate = dates.get(this.initialEndDate)
      const now = dates.now()

      // Check for common presets
      const presets = [
        {
          label: 'This Week',
          check: () => {
            const week = dates.getWeek(0)
            const [monday, , , , , , sunday] = week
            return startDate.isSame(monday, 'day') && endDate.isSame(sunday, 'day')
          },
        },
        {
          label: 'Last Week',
          check: () => {
            const week = dates.getWeek(-1)
            const [monday, , , , , , sunday] = week
            return startDate.isSame(monday, 'day') && endDate.isSame(sunday, 'day')
          },
        },
        {
          label: 'This Month',
          check: () => {
            const monthStart = now.startOf('month')
            const monthEnd = now.endOf('month')
            return startDate.isSame(monthStart, 'day') && endDate.isSame(monthEnd, 'day')
          },
        },
        {
          label: 'Last Month',
          check: () => {
            const lastMonth = now.subtract(1, 'month')
            const monthStart = lastMonth.startOf('month')
            const monthEnd = lastMonth.endOf('month')
            return startDate.isSame(monthStart, 'day') && endDate.isSame(monthEnd, 'day')
          },
        },
        {
          label: 'MTD',
          check: () => {
            const monthStart = now.startOf('month')
            return startDate.isSame(monthStart, 'day') && endDate.isSame(now, 'day')
          },
        },
        {
          label: 'YTD',
          check: () => {
            const yearStart = now.startOf('year')
            return startDate.isSame(yearStart, 'day') && endDate.isSame(now, 'day')
          },
        },
      ]

      const matchingPreset = presets.find((preset) => preset.check())
      return matchingPreset ? matchingPreset.label : null
    },

    onApplyDateRange(dateRange) {
      this.menuOpen = false
      this.$emit('apply-date-range', dateRange)
    },

    onClearDateRange() {
      this.menuOpen = false
      this.$emit('clear-date-range')
    },
  },
}
</script>

<style lang="scss" scoped>
@import "~src/styles/variables";

.date-range-button {
  font-weight: 500;
  letter-spacing: 0.25px;
  min-width: 220px;
  max-width: 320px;
  justify-content: flex-start;
  text-transform: none;
  height: 44px;
  border-radius: 24px;
  box-shadow: 0 2px 8px rgba(25, 118, 210, 0.07);
  background: linear-gradient(90deg, #f5fafd 0%, #f7faff 100%);

  &:hover {
    box-shadow: 0 4px 16px rgba(25, 118, 210, 0.12);
    transform: translateY(-1px);
    transition: all 0.2s ease;
  }
}

.theme--dark .date-range-button {
  background: linear-gradient(90deg, #2d2d2d 0%, #353535 100%);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);

  &:hover {
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.5);
  }
}

.date-range-dropdown-card {
  border-radius: 16px !important;
  box-shadow: 0 8px 32px rgba(25, 118, 210, 0.13) !important;
  overflow: hidden;
  background: #fafdff;
}

.date-range-header {
  background: linear-gradient(135deg, rgba(25, 118, 210, 0.08) 0%, rgba(25, 118, 210, 0.02) 100%);
  border-bottom: 1px solid rgba(25, 118, 210, 0.08);
}

.v-card__text {
  background: rgba(255, 255, 255, 0.98);
}

::v-deep .v-menu__content {
  border-radius: 16px !important;
  box-shadow: 0 8px 32px rgba(25, 118, 210, 0.13) !important;
  backdrop-filter: blur(10px);
}

.theme--dark {
  .date-range-dropdown-card {
    background: #232a33;
    .date-range-header {
      background: linear-gradient(135deg, rgba(25, 118, 210, 0.18) 0%, rgba(25, 118, 210, 0.09) 100%);
      border-bottom-color: rgba(255, 255, 255, 0.10);
    }
    .v-card__text {
      background: rgba(48, 48, 48, 0.98);
    }
  }
}

@media (max-width: 768px) {
  .date-range-button {
    min-width: 180px;
    max-width: 100%;
    width: 100%;
    font-size: 0.95rem;
    height: 40px;
    border-radius: 20px;
    padding-left: 12px;
    padding-right: 12px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}
</style>
